import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Receipt } from "lucide-react";

const CATEGORIES = ["Office", "Travel", "Salaries", "Utilities", "Rent", "Software", "Tax", "Marketing", "Other"];
const MODES = ["Cash", "Bank", "UPI", "Cheque", "Card"];

const empty = () => ({ title: "", category: "Office", amount: 0, date: new Date().toISOString().slice(0, 10), payment_mode: "Cash", bank_id: "", vendor: "", notes: "" });

export default function Expenses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [filterCat, setFilterCat] = useState("all");

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/expenses"), api.get("/banks")]);
    setItems(a.data);
    setBanks(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount), bank_id: form.bank_id || null };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const filtered = filterCat === "all" ? items : items.filter(i => i.category === filterCat);
  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);
  const cashTotal = filtered.filter(i => (i.payment_mode || "").toLowerCase() === "cash").reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Money</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">Expenses</h1>
          <p className="text-zinc-600 mt-2">Track every rupee going out.</p>
        </div>
        {can(user, "invoices_write") && (
          <button onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }} data-testid="new-expense" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800">
            <Plus size={16} /> New Expense
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-caps mb-2">Filtered Total</p>
          <div className="font-display font-black text-3xl tracking-tighter text-[#D92D20]">₹{total.toLocaleString("en-IN")}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-caps mb-2">Cash Outflow</p>
          <div className="font-display font-black text-3xl tracking-tighter">₹{cashTotal.toLocaleString("en-IN")}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <p className="label-caps mb-2">Entries</p>
          <div className="font-display font-black text-3xl tracking-tighter">{filtered.length}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilterCat("all")} className={`px-3 py-1.5 text-sm font-semibold border ${filterCat === "all" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>All</button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setFilterCat(c)} className={`px-3 py-1.5 text-sm font-semibold border ${filterCat === c ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>{c}</button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-6 py-3 label-caps">Date</th>
              <th className="text-left px-6 py-3 label-caps">Title</th>
              <th className="text-left px-6 py-3 label-caps">Category</th>
              <th className="text-left px-6 py-3 label-caps">Vendor</th>
              <th className="text-left px-6 py-3 label-caps">Mode</th>
              <th className="text-right px-6 py-3 label-caps">Amount</th>
              <th className="text-right px-6 py-3 label-caps"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-zinc-500 font-mono text-sm">[ no expenses ]</td></tr>}
            {filtered.map(e => (
              <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-6 py-3 text-sm font-mono">{e.date}</td>
                <td className="px-6 py-3 text-sm font-medium">{e.title}</td>
                <td className="px-6 py-3 text-sm">{e.category}</td>
                <td className="px-6 py-3 text-sm text-zinc-600">{e.vendor || "—"}</td>
                <td className="px-6 py-3 text-sm">{e.payment_mode}</td>
                <td className="px-6 py-3 text-sm font-mono font-bold text-right">₹{e.amount?.toLocaleString("en-IN")}</td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  {can(user, "invoices_write") && <button onClick={() => { setEditing(e); setForm({ ...empty(), ...e, bank_id: e.bank_id || "" }); setOpen(true); }} className="p-1.5 hover:bg-zinc-100 mr-1"><Pencil size={14} /></button>}
                  {can(user, "delete") && <button onClick={() => remove(e.id)} className="p-1.5 hover:bg-red-50 text-[#D92D20]"><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={ev => ev.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Expense" : "New Expense"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="label-caps block mb-2">Title *</label>
                <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="expense-title" className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">Category *</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-2">Date *</label>
                  <input required type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">Amount *</label>
                  <input required type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono" />
                </div>
                <div>
                  <label className="label-caps block mb-2">Payment Mode</label>
                  <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    {MODES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              {form.payment_mode !== "Cash" && (
                <div>
                  <label className="label-caps block mb-2">Bank Account</label>
                  <select value={form.bank_id} onChange={e => setForm({ ...form, bank_id: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="">Select bank</option>
                    {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_number}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="label-caps block mb-2">Vendor</label>
                <input value={form.vendor || ""} onChange={e => setForm({ ...form, vendor: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div>
                <label className="label-caps block mb-2">Notes</label>
                <textarea rows="2" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-expense" className="bg-zinc-900 text-white px-4 py-2 font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
